import type { User } from 'firebase/auth';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { analyticsClient } from '@/lib';
import { favoriteService } from '@/services';

const EMPTY_UIDS: string[] = [];

/** The viewer's favorited uids plus an optimistic `toggleFavorite`. Private to the viewer. */
export function useFavorites(user: User | null) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<Error | null>(null);

  const { data } = useQuery<string[]>({
    queryKey: ['favoriteUids', user?.uid],
    // placeholder only; subscription fills the cache
    queryFn: () => EMPTY_UIDS,
    enabled: false,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (!user) return;

    const unsubscribe = favoriteService.subscribeToMyFavorites(
      user.uid,
      toUids => {
        setError(null);
        queryClient.setQueryData(['favoriteUids', user.uid], toUids);
      },
      setError
    );

    return () => unsubscribe();
  }, [user, queryClient]);

  const favoriteUids = user ? (data ?? EMPTY_UIDS) : EMPTY_UIDS;
  const favoriteSet = useMemo(() => new Set(favoriteUids), [favoriteUids]);
  const isFavorite = useCallback((uid: string) => favoriteSet.has(uid), [favoriteSet]);

  // Ref guards in-flight writes; state mirrors it for the UI.
  const pendingRef = useRef(new Set<string>());
  const [pendingUids, setPendingUids] = useState<Set<string>>(new Set());
  const isPending = useCallback((uid: string) => pendingUids.has(uid), [pendingUids]);

  /** True when the write succeeded; lets callers toast on the result. */
  const toggleFavorite = useCallback(
    async (targetUid: string): Promise<boolean> => {
      if (!user || targetUid === user.uid || pendingRef.current.has(targetUid)) return false;

      const key = ['favoriteUids', user.uid];
      const wasFavorite = favoriteSet.has(targetUid);
      const withFavorite = (old: string[] | undefined, on: boolean) => {
        const next = new Set(old);
        if (on) next.add(targetUid);
        else next.delete(targetUid);
        return [...next].sort();
      };

      pendingRef.current.add(targetUid);
      setPendingUids(new Set(pendingRef.current));
      // Optimistic flip; the subscription snapshot reconciles on ack.
      queryClient.setQueryData<string[]>(key, old => withFavorite(old, !wasFavorite));
      try {
        if (wasFavorite) {
          await favoriteService.unfavorite(user.uid, targetUid);
        } else {
          await favoriteService.favorite(user.uid, targetUid);
        }
        analyticsClient.capture(wasFavorite ? 'profile_unfavorited' : 'profile_favorited', {
          user_id: user.uid,
          target_id: targetUid,
          session_id: analyticsClient.getCurrentSessionId(),
        });
        return true;
      } catch (err) {
        queryClient.setQueryData<string[]>(key, old => withFavorite(old, wasFavorite));
        console.error('Favorite toggle failure:', err);
        return false;
      } finally {
        pendingRef.current.delete(targetUid);
        setPendingUids(new Set(pendingRef.current));
      }
    },
    [user, favoriteSet, queryClient]
  );

  return {
    favoriteUids,
    isFavorite,
    isPending,
    toggleFavorite,
    isLoading: Boolean(user && data === undefined && !error),
    isError: error != null,
    error,
  };
}
